import React, { useState } from 'react';
import { 
    SparklesIcon, 
    CheckIcon,
    EyeIcon,
    StarIcon
} from '@heroicons/react/24/outline';

const TemplateSelector = ({ templates, onSelect, onLinkedInImport, linkedinCode }) => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [hoveredTemplate, setHoveredTemplate] = useState(null);

    const categories = [
        { id: 'all', name: 'Tous', icon: SparklesIcon },
        { id: 'moderne', name: 'Moderne', icon: StarIcon },
        { id: 'élégant', name: 'Élégant', icon: EyeIcon },
        { id: 'créatif', name: 'Créatif', icon: SparklesIcon },
        { id: 'minimal', name: 'Minimal', icon: EyeIcon },
        { id: 'professionnel', name: 'Professionnel', icon: StarIcon },
        { id: 'audacieux', name: 'Audacieux', icon: SparklesIcon }
    ];

    const filteredTemplates = selectedCategory === 'all' 
        ? templates 
        : templates.filter(template => template.category === selectedCategory);

    const getTemplatePreview = (template) => {
        // Aperçus générés dynamiquement basés sur le template
        const previews = {
            'modern-blue': (
                <div className="w-full h-48 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                        <div className="w-16 h-2 bg-blue-600 rounded"></div>
                        <div className="w-8 h-8 bg-blue-600 rounded-full"></div>
                    </div>
                    <div className="space-y-2">
                        <div className="w-3/4 h-3 bg-blue-600 rounded"></div>
                        <div className="w-1/2 h-2 bg-gray-400 rounded"></div>
                        <div className="w-2/3 h-2 bg-gray-300 rounded"></div>
                        <div className="w-1/3 h-2 bg-gray-300 rounded"></div>
                    </div>
                    <div className="mt-4 space-y-1">
                        <div className="w-full h-1 bg-blue-200 rounded"></div>
                        <div className="w-3/4 h-1 bg-blue-200 rounded"></div>
                    </div>
                </div>
            ),
            'elegant-dark': (
                <div className="w-full h-48 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg p-4 border border-gray-700">
                    <div className="flex items-center justify-between mb-3">
                        <div className="w-16 h-2 bg-white rounded"></div>
                        <div className="w-8 h-8 bg-white rounded-full"></div>
                    </div>
                    <div className="space-y-2">
                        <div className="w-3/4 h-3 bg-white rounded"></div>
                        <div className="w-1/2 h-2 bg-gray-400 rounded"></div>
                        <div className="w-2/3 h-2 bg-gray-500 rounded"></div>
                        <div className="w-1/3 h-2 bg-gray-500 rounded"></div>
                    </div>
                    <div className="mt-4 space-y-1">
                        <div className="w-full h-1 bg-gray-600 rounded"></div>
                        <div className="w-3/4 h-1 bg-gray-600 rounded"></div>
                    </div>
                </div>
            ),
            'creative-gradient': (
                <div className="w-full h-48 bg-gradient-to-br from-purple-400 via-pink-400 to-purple-600 rounded-lg p-4 border border-purple-300">
                    <div className="flex items-center justify-between mb-3">
                        <div className="w-16 h-2 bg-white rounded-full"></div>
                        <div className="w-8 h-8 bg-white rounded-full"></div>
                    </div>
                    <div className="space-y-2">
                        <div className="w-3/4 h-3 bg-white rounded-full"></div>
                        <div className="w-1/2 h-2 bg-purple-200 rounded-full"></div>
                        <div className="w-2/3 h-2 bg-pink-200 rounded-full"></div>
                        <div className="w-1/3 h-2 bg-purple-200 rounded-full"></div>
                    </div>
                    <div className="mt-4 space-y-1">
                        <div className="w-full h-1 bg-white rounded-full"></div>
                        <div className="w-3/4 h-1 bg-white rounded-full"></div>
                    </div>
                </div>
            ),
            'minimal-clean': (
                <div className="w-full h-48 bg-white rounded-lg p-4 border-2 border-green-200">
                    <div className="flex items-center justify-between mb-3">
                        <div className="w-16 h-2 bg-green-600 rounded"></div>
                        <div className="w-8 h-8 border-2 border-green-600 rounded-full"></div>
                    </div>
                    <div className="space-y-2">
                        <div className="w-3/4 h-3 bg-gray-800 rounded"></div>
                        <div className="w-1/2 h-2 bg-gray-400 rounded"></div>
                        <div className="w-2/3 h-2 bg-gray-300 rounded"></div>
                        <div className="w-1/3 h-2 bg-gray-300 rounded"></div>
                    </div>
                    <div className="mt-4 space-y-1">
                        <div className="w-full h-1 bg-green-200 rounded"></div>
                        <div className="w-3/4 h-1 bg-green-200 rounded"></div>
                    </div>
                </div>
            ),
            'professional-gray': (
                <div className="w-full h-48 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-300">
                    <div className="flex items-center justify-between mb-3">
                        <div className="w-16 h-2 bg-gray-600 rounded"></div>
                        <div className="w-8 h-8 bg-gray-600 rounded"></div>
                    </div>
                    <div className="space-y-2">
                        <div className="w-3/4 h-3 bg-gray-700 rounded"></div>
                        <div className="w-1/2 h-2 bg-gray-500 rounded"></div>
                        <div className="w-2/3 h-2 bg-gray-400 rounded"></div>
                        <div className="w-1/3 h-2 bg-gray-400 rounded"></div>
                    </div>
                    <div className="mt-4 space-y-1">
                        <div className="w-full h-1 bg-gray-300 rounded"></div>
                        <div className="w-3/4 h-1 bg-gray-300 rounded"></div>
                    </div>
                </div>
            ),
            'bold-red': (
                <div className="w-full h-48 bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
                    <div className="flex items-center justify-between mb-3">
                        <div className="w-16 h-2 bg-red-600 rounded"></div>
                        <div className="w-8 h-8 bg-red-600 rounded"></div>
                    </div>
                    <div className="space-y-2">
                        <div className="w-3/4 h-3 bg-red-600 rounded"></div>
                        <div className="w-1/2 h-2 bg-gray-600 rounded"></div>
                        <div className="w-2/3 h-2 bg-gray-500 rounded"></div>
                        <div className="w-1/3 h-2 bg-gray-500 rounded"></div>
                    </div>
                    <div className="mt-4 space-y-1">
                        <div className="w-full h-1 bg-red-300 rounded"></div>
                        <div className="w-3/4 h-1 bg-red-300 rounded"></div>
                    </div>
                </div>
            ),
            'creative-purple': (
                <div className="w-full h-48 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                    <div className="flex items-center justify-between mb-3">
                        <div className="w-16 h-2 bg-purple-600 rounded-full"></div>
                        <div className="w-8 h-8 bg-purple-600 rounded-full"></div>
                    </div>
                    <div className="space-y-2">
                        <div className="w-3/4 h-3 bg-purple-600 rounded-full"></div>
                        <div className="w-1/2 h-2 bg-purple-400 rounded-full"></div>
                        <div className="w-2/3 h-2 bg-purple-300 rounded-full"></div>
                        <div className="w-1/3 h-2 bg-purple-300 rounded-full"></div>
                    </div>
                    <div className="mt-4 space-y-1">
                        <div className="w-full h-1 bg-purple-300 rounded-full"></div>
                        <div className="w-3/4 h-1 bg-purple-300 rounded-full"></div>
                    </div>
                </div>
            ),
            'modern-green': (
                <div className="w-full h-48 bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center justify-between mb-3">
                        <div className="w-16 h-2 bg-green-600 rounded"></div>
                        <div className="w-8 h-8 bg-green-600 rounded-full"></div>
                    </div>
                    <div className="space-y-2">
                        <div className="w-3/4 h-3 bg-green-600 rounded"></div>
                        <div className="w-1/2 h-2 bg-gray-600 rounded"></div>
                        <div className="w-2/3 h-2 bg-gray-500 rounded"></div>
                        <div className="w-1/3 h-2 bg-gray-500 rounded"></div>
                    </div>
                    <div className="mt-4 space-y-1">
                        <div className="w-full h-1 bg-green-300 rounded"></div>
                        <div className="w-3/4 h-1 bg-green-300 rounded"></div>
                    </div>
                </div>
            ),
            'elegant-red': (
                <div className="w-full h-48 bg-gradient-to-br from-red-50 to-red-100 rounded-lg p-4 border border-red-200">
                    <div className="flex items-center justify-between mb-3">
                        <div className="w-16 h-2 bg-red-600 rounded"></div>
                        <div className="w-8 h-8 bg-red-600 rounded"></div>
                    </div>
                    <div className="space-y-2">
                        <div className="w-3/4 h-3 bg-red-600 rounded"></div>
                        <div className="w-1/2 h-2 bg-gray-600 rounded"></div>
                        <div className="w-2/3 h-2 bg-gray-500 rounded"></div>
                        <div className="w-1/3 h-2 bg-gray-500 rounded"></div>
                    </div>
                    <div className="mt-4 space-y-1">
                        <div className="w-full h-1 bg-red-300 rounded"></div>
                        <div className="w-3/4 h-1 bg-red-300 rounded"></div>
                    </div>
                </div>
            ),
            'minimal-blue': (
                <div className="w-full h-48 bg-white rounded-lg p-4 border-2 border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                        <div className="w-16 h-2 bg-blue-600 rounded"></div>
                        <div className="w-8 h-8 border-2 border-blue-600 rounded-full"></div>
                    </div>
                    <div className="space-y-2">
                        <div className="w-3/4 h-3 bg-gray-800 rounded"></div>
                        <div className="w-1/2 h-2 bg-gray-400 rounded"></div>
                        <div className="w-2/3 h-2 bg-gray-300 rounded"></div>
                        <div className="w-1/3 h-2 bg-gray-300 rounded"></div>
                    </div>
                    <div className="mt-4 space-y-1">
                        <div className="w-full h-1 bg-blue-200 rounded"></div>
                        <div className="w-3/4 h-1 bg-blue-200 rounded"></div>
                    </div>
                </div>
            )
        };

        return previews[template.id] || previews['modern-blue'];
    };

    return (
        <div className="p-4 md:p-8 overflow-x-hidden min-w-0">
            {/* Header */}
            <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                    Choisissez votre template
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto mb-6">
                    Sélectionnez un design qui correspond à votre personnalité et au secteur d'activité ciblé
                </p>
                
                {/* Bouton LinkedIn temporairement masqué - en attente du support LinkedIn */}
                {/* 
                <div className="flex justify-center">
                    <button
                        onClick={() => setShowLinkedInImport(true)}
                        className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                    >
                        <LinkIcon className="w-5 h-5" />
                        Importer depuis LinkedIn
                    </button>
                </div>
                */}
            </div>

            {/* Categories Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-8">
                {categories.map((category) => (
                    <button
                        key={category.id}
                        onClick={() => setSelectedCategory(category.id)}
                        className={`flex items-center px-4 py-2 rounded-full border-2 transition-all duration-300 ${
                            selectedCategory === category.id
                                ? 'border-blue-600 bg-blue-600 text-white'
                                : 'border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600'
                        }`}
                    >
                        <category.icon className="w-4 h-4 mr-2" />
                        {category.name}
                    </button>
                ))}
            </div>

            {/* Templates Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 overflow-hidden min-w-0">
                {filteredTemplates.map((template) => (
                    <div
                        key={template.id}
                        className={`relative group cursor-pointer transition-all duration-300 transform md:hover:scale-105 max-w-full min-w-0 ${
                            hoveredTemplate === template.id ? 'ring-4 ring-blue-500' : ''
                        }`}
                        onMouseEnter={() => setHoveredTemplate(template.id)}
                        onMouseLeave={() => setHoveredTemplate(null)}
                        onClick={() => onSelect(template)}
                    >
                        {/* Template Preview */}
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
                            {getTemplatePreview(template)}
                            
                            {/* Template Info */}
                            <div className="p-4">
                                <div className="flex items-center justify-between mb-2">
                                    <h3 className="text-lg font-semibold text-gray-800">
                                        {template.name}
                                    </h3>
                                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded-full capitalize">
                                        {template.category}
                                    </span>
                                </div>
                                
                                {/* Color Preview */}
                                <div className="flex items-center space-x-2 mb-3">
                                    <div 
                                        className="w-4 h-4 rounded-full border border-gray-300"
                                        style={{ backgroundColor: template.colors.primary }}
                                    ></div>
                                    <div 
                                        className="w-4 h-4 rounded-full border border-gray-300"
                                        style={{ backgroundColor: template.colors.secondary }}
                                    ></div>
                                    <span className="text-xs text-gray-500">
                                        {template.font}
                                    </span>
                                </div>

                                {/* Features */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                                        <span>✓ Responsive</span>
                                        <span>✓ ATS Friendly</span>
                                    </div>
                                    <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                                        Utiliser
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Hover Overlay */}
                        {hoveredTemplate === template.id && (
                            <div className="absolute inset-0 bg-blue-600 bg-opacity-90 rounded-2xl flex items-center justify-center">
                                <div className="text-center text-white">
                                    <CheckIcon className="w-8 h-8 mx-auto mb-2" />
                                    <p className="font-semibold">Sélectionner ce template</p>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Tips */}
            <div className="mt-12 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                <div className="flex items-start">
                    <SparklesIcon className="w-6 h-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">
                            Conseils pour choisir votre template
                        </h3>
                        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                            <div>
                                <p className="font-medium mb-1">🎨 <strong>Moderne/Créatif :</strong></p>
                                <p>Parfait pour les secteurs tech, design, marketing</p>
                            </div>
                            <div>
                                <p className="font-medium mb-1">💼 <strong>Professionnel/Élégant :</strong></p>
                                <p>Idéal pour la finance, le droit, la médecine</p>
                            </div>
                            <div>
                                <p className="font-medium mb-1">📋 <strong>Minimal :</strong></p>
                                <p>Excellent pour les postes administratifs, l'éducation</p>
                            </div>
                            <div>
                                <p className="font-medium mb-1">🚀 <strong>Audacieux :</strong></p>
                                <p>Parfait pour les ventes, l'entrepreneuriat</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal LinkedIn temporairement masqué - en attente du support LinkedIn */}
            {/* 
            <LinkedInImport
                isVisible={showLinkedInImport}
                onClose={() => setShowLinkedInImport(false)}
                onImportData={(linkedinData) => {
                    setShowLinkedInImport(false);
                    onLinkedInImport(linkedinData);
                }}
                initialCode={linkedinCode}
            />
            */}
        </div>
    );
};

export default TemplateSelector; 